import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    Paper,
    Stack,
    LinearProgress,
    Avatar,
    CircularProgress,
} from "@mui/material";
import {
    VolumeUp,
    Mic,
    MicOff,
    CheckCircle,
    Cancel,
    PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { interviewApi } from "../../api/interviewApi";
import CustomButton from "../../components/common/CustomButton";
import CameraModal from "../../components/common/CameraModal";
import CustomSnackbar from "../../components/common/CustomSnackbar";

const InterviewInstructionPage = () => {
    const [statuses, setStatuses] = useState({
        audio: "pending",
        mic: "pending",
        camera: "pending",
    });
    const [answer, setAnswer] = useState("");
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [listening, setListening] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showCameraCheck, setShowCameraCheck] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "",
    });

    const recognitionRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation()
    // console.log(location.state)
    const mode = location.state?.mode;
    const candidateId = location.state?.candidate_id;
    const InterviewStatus = location.state?.Interview_status


    // const interview_id = sessionStorage.getItem("interview_id");
    // const interview_status = sessionStorage.getItem("Interview_status");

    const setStatus = (key, value) =>
        setStatuses((prev) => ({ ...prev, [key]: value }));

    const tests = Object.values(statuses);
    const totalDone = tests.filter((s) => s === "success").length;
    const progress = (totalDone / 3) * 100;
    const allTestsDone = totalDone === 3;

    const showSnack = (message, severity = "info") =>
        setSnackbar({ open: true, message, severity });

    /** 🔊 AUDIO TEST */
    const playSampleQuestion = () => {
        try {
            setIsPlayingAudio(true);
            const utterance = new SpeechSynthesisUtterance(
                "This is a sample question. Please ensure your speakers or headphones are working properly."
            );
            utterance.onend = () => {
                setIsPlayingAudio(false);
                setStatus("audio", "success");
            };
            speechSynthesis.speak(utterance);
        } catch (err) {
            console.error(err);
            setIsPlayingAudio(false);
            setStatus("audio", "fail");
            showSnack("Audio playback failed.", "error");
        }
    };

    /** 🎙️ MIC TEST */
    const startListening = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;

            const SpeechRecognition =
                window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) {
                setStatus("mic", "fail");
                return showSnack("Speech recognition not supported.", "error");
            }

            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.lang = "en-US";
            recognitionRef.current.interimResults = true;
            recognitionRef.current.continuous = true;

            let transcript = "";

            recognitionRef.current.onresult = (event) => {
                let interim = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const t = event.results[i][0].transcript;
                    if (event.results[i].isFinal) transcript += t + " ";
                    else interim += t;
                }
                setAnswer(transcript + interim);
            };

            recognitionRef.current.onend = () => {
                setStatus("mic", transcript.trim() ? "success" : "fail");
                stopListening();
            };

            recognitionRef.current.start();
            setListening(true);
        } catch (err) {
            console.error(err);
            setStatus("mic", "fail");
            showSnack("Microphone access denied.", "error");
        }
    };

    const stopListening = () => {
        recognitionRef.current?.stop();
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        setListening(false);
    };

    /** 🎥 CAMERA CHECK */
    const handleCameraClose = (photoUrl) => {
        setShowCameraCheck(false);
        setStatus("camera", "success");

    };

    /** 🚀 START INTERVIEW */
    const handleStartInterview = async () => {
        if (!allTestsDone)
            return showSnack(
                "Please complete all tests before starting the interview.",
                "warning"
            );

        setLoading(true);
        try {
            const response = await interviewApi.start();
            // console.log("Start interview response:", response);

            const { status, code, message } = response || {};


            /* 1️⃣ Network error */
            if (status === 0) {
                showSnack(
                    "Network error. Please check your internet connection.",
                    "warning"
                );
                return;
            }

            /* 2️⃣ Interview already completed / in progress */
            if (status === 208) {
                showSnack(
                    message || "Interview already completed or in progress.",
                    "warning"
                );
                return;
            }

            /* 3️⃣ Server / client errors */
            if (status >= 400) {
                showSnack(
                    message || "Error starting interview.",
                    "error"
                );
                return;
            }

            /* 4️⃣ Success */
            if (status === 200 && code === "success") {
                if (mode === "dynamic") {
                    navigate("/flowiseInterview", {
                        state: {
                            "interview_status": InterviewStatus,
                            "candidate_id": candidateId
                        },
                        replace: true
                    });
                } else {
                    navigate("/interview", {
                        state: {
                            "interview_status": InterviewStatus,
                            "candidate_id": candidateId
                        },
                        replace: true
                    });
                }
                return;
            }

            /* 5️⃣ Fallback */
            showSnack("Unexpected response from server.", "error");



        } catch (err) {
            console.error("Failed to start interview:", err);
            showSnack("Failed to start interview, please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const getColor = (status) =>
        status === "success" ? "#22c55e" : status === "fail" ? "#ef4444" : "#d1d5db";

    return (
        <Box
            sx={{
                backgroundColor: "#f8fafc",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 5,
                px: 2,
            }}
        >
            {/* 🎥 Camera Check */}
            <CameraModal
                open={showCameraCheck}
                payload={{ "candidate_id": candidateId }}
                onClose={handleCameraClose}
            />

            {/* Header */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#f8fafc",
                    pb: 2,
                    width: "100%",
                    maxWidth: 700,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    borderRadius: 2,
                    px: 2,
                }}
            >
                <Typography variant="h5" fontWeight={700} color="#111827">
                    System Check
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Verify your equipment before the interview begins
                </Typography>

                <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                        Setup Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {Math.round(progress)}%
                    </Typography>
                </Stack>

                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 8, borderRadius: 2 }}
                />

                <Stack direction="row" justifyContent="space-around" mt={2}>
                    {[
                        { label: "Camera", status: statuses.camera },
                        { label: "Audio", status: statuses.audio },
                        { label: "Mic", status: statuses.mic },
                    ].map(({ label, status }, i) => (
                        <Stack key={i} alignItems="center" spacing={0.5}>
                            <Avatar sx={{ bgcolor: getColor(status), width: 34, height: 34 }}>
                                {status === "success" ? (
                                    <CheckCircle fontSize="small" />
                                ) : status === "fail" ? (
                                    <Cancel fontSize="small" />
                                ) : (
                                    <Typography variant="caption">{i + 1}</Typography>
                                )}
                            </Avatar>
                            <Typography
                                variant="caption"
                                sx={{
                                    color:
                                        status === "fail"
                                            ? "#ef4444"
                                            : status === "success"
                                                ? "#22c55e"
                                                : "#6b7280",
                                }}
                            >
                                {label}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>

            {/* Tests */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 700,
                    mt: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                {/* 🔊 Audio Test */}
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                        <VolumeUp sx={{ color: "#2563eb" }} />
                        <Typography variant="h6" fontWeight={600}>
                            Audio Test
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Test your speakers or headphones with a sample question.
                    </Typography>
                    <Button
                        variant="contained"
                        disabled={isPlayingAudio}
                        sx={{
                            backgroundColor: isPlayingAudio ? "#9ca3af" : "#000",
                            "&:hover": { backgroundColor: "#333" },
                            width: 215,
                        }}
                        onClick={playSampleQuestion}
                    >
                        {isPlayingAudio ? "Playing..." : "Play Sample Question"}
                    </Button>
                </Paper>

                {/* 🎙️ Mic Test */}
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                        {listening ? (
                            <Mic sx={{ color: "#22c55e" }} />
                        ) : (
                            <MicOff sx={{ color: "#9ca3af" }} />
                        )}
                        <Typography variant="h6" fontWeight={600}>
                            Microphone Test
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Click ‘Start Mic Test’, read the sentence below clearly and loudly, then click ‘Stop Mic Test’ to finish.                    <br />
                        <b>“My voice is clear, and I am ready to confidently showcase my skills today.”</b>
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#000",
                            "&:hover": { backgroundColor: "#333" },
                            width: 215,
                            mb: 2,
                        }}
                        onClick={listening ? stopListening : startListening}
                    >
                        {listening ? "Stop Mic Test" : "Start Mic Test"}
                    </Button>
                    <Typography
                        variant="body2"
                        sx={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            p: 2,
                            minHeight: "60px",
                            color: answer ? "#111827" : "#9ca3af",
                            fontStyle: answer ? "normal" : "italic",
                        }}
                    >
                        {answer || "Your transcribed response will appear here..."}
                    </Typography>
                </Paper>
            </Box>

            {/* 🚀 Start Interview */}
            <CustomButton
                onClick={handleStartInterview}
                disabled={loading || !allTestsDone}
                sx={{
                    minWidth: 180,
                    height: 48,
                    fontWeight: "bold",
                    borderRadius: 3,
                    fontSize: "1rem",
                    mt: 4,
                    boxShadow: "0 6px 20px rgba(67,160,71,0.4)",
                }}
            >
                {loading ? (
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <CircularProgress size={20} color="inherit" />
                        <span>Initializing...</span>
                    </Box>
                ) : (
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <PlayArrowIcon sx={{ fontSize: 22 }} />
                        <span>Start Interview</span>
                    </Box>
                )}
            </CustomButton>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={6000}
            />
        </Box>
    );
};

export default InterviewInstructionPage;
